/**
 * The first-run "How to play" card (`q-demo-touch-first-run-onboarding`; guest-demo.md §5.7.4).
 * The rules and the words are in `howToPlay.ts`; this file renders a plan and reports the answers.
 *
 * It is the one deliberate stop in the demo's first seconds: a centred card over the world with
 * one big "Let's go". On a touch device that has never chosen the on-screen controls it also asks,
 * up front, whether to switch them on (the same setting Settings writes, so the answer is kept).
 *
 * Layout (q-how-to-play-lets-go-below-fold-landscape): the explanations scroll inside
 * `.hud-howto-body`; everything the player must ANSWER (the controls offer and "Let's go") sits in
 * `.hud-howto-actions`, outside that scroller, so a short screen never hides the card's only exit
 * below a fold. On a phone held sideways (844×390) the two sit side by side (theme.css), so the
 * whole card fits without scrolling at all.
 */

import { CONTROLS_OFFER, type HowToPlayPlan } from './howToPlay.js';

export interface HowToPlayCardProps {
  plan: HowToPlayPlan;
  /** The controls offer's answer so far: null = not answered yet. */
  controlsAnswer: boolean | null;
  onControls: (on: boolean) => void;
  onGo: () => void;
}

export function HowToPlayCard({
  plan,
  controlsAnswer,
  onControls,
  onGo,
}: HowToPlayCardProps): JSX.Element {
  return (
    <div className="hud-howto-scrim interactive" data-testid="how-to-play" data-input={plan.input}>
      <div className="hud-howto" role="dialog" aria-modal="true" aria-labelledby="hud-howto-title">
        <div className="hud-howto-body" data-testid="how-to-play-body">
          <h2 id="hud-howto-title" className="hud-howto-title">
            {plan.title}
          </h2>
          <ul className="hud-howto-rows">
            {plan.rows.map((r) => (
              <li key={r.id} className="hud-howto-row" data-row={r.id}>
                <span className="hud-howto-label">{r.label}</span>
                <span className="hud-howto-text">{r.text}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="hud-howto-actions" data-testid="how-to-play-actions">
          {plan.offerControls ? (
            <div className="hud-howto-offer" data-testid="how-to-play-controls">
              <div className="hud-howto-question">{CONTROLS_OFFER.question}</div>
              <div className="hud-howto-choices">
                <button
                  type="button"
                  className={`pxl-btn${controlsAnswer === true ? ' primary' : ''}`}
                  aria-pressed={controlsAnswer === true}
                  data-testid="how-to-play-controls-on"
                  onClick={() => onControls(true)}
                >
                  {CONTROLS_OFFER.yes}
                </button>
                <button
                  type="button"
                  className={`pxl-btn${controlsAnswer === false ? ' primary' : ''}`}
                  aria-pressed={controlsAnswer === false}
                  data-testid="how-to-play-controls-off"
                  onClick={() => onControls(false)}
                >
                  {CONTROLS_OFFER.no}
                </button>
              </div>
              <div className="pxl-faint">{CONTROLS_OFFER.later}</div>
            </div>
          ) : null}
          <button
            type="button"
            className="pxl-btn primary hud-howto-go"
            data-testid="how-to-play-go"
            onClick={onGo}
          >
            {plan.goLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
